import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Table, Button, Form, Spinner } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaMicrophone } from "react-icons/fa";
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import ConfirmModal from "../../UI/ConfirmModal";

const CertificateManagement = () => {
    const { t } = useTranslation();
    const [certificates, setCertificates] = useState([]);
    const [certificateNames, setCertificateNames] = useState([]);
    const [filterName, setFilterName] = useState("");
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [isListening, setIsListening] = useState(false);

    const [formData, setFormData] = useState({
        cerName: "",
        description: "",
    });

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [idToDelete, setIdToDelete] = useState(null);

    const fetchCertificates = async () => {
        const token = getAuthToken();
        const apiChar = getApiChara();
        setLoading(true);
        try {
            const response = await fetch(`${apiChar}/certificate/getAllCertificates`, {
                headers: { Authorization: "Bearer " + token },
            });
            if (response.ok) {
                const data = await response.json();
                setCertificates(data || []);
            }
        } catch (error) {
            console.error("Error fetching certificates:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCertificateNames = async () => {
        const token = getAuthToken();
        const apiChar = getApiChara();
        try {
            const response = await fetch(`${apiChar}/certificate/getAllCertificateNames`, {
                headers: { Authorization: "Bearer " + token },
            });
            if (response.ok) {
                const data = await response.json();
                setCertificateNames(data || []);
            }
        } catch (error) {
            console.error("Error fetching certificate names:", error);
        }
    };

    useEffect(() => {
        fetchCertificates();
        fetchCertificateNames();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleQuillChange = (content) => {
        setFormData(prev => ({ ...prev, description: content }));
    };

    const handleVoiceInput = () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            alert("Speech Recognition API is not supported in this browser. Please use Chrome.");
            return;
        }

        if (isListening) return;

        const recognition = new SpeechRecognition();
        recognition.lang = 'en-IN';
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;

        recognition.onstart = () => setIsListening(true);
        recognition.onresult = (event) => {
            const transcript = event.results[0][0].transcript;
            if (transcript) {
                setFormData(prev => {
                    const prevDesc = prev.description || "";
                    const cleanPrev = prevDesc.replace(/<[^>]*>?/gm, '').trim();
                    if (cleanPrev === "") return { ...prev, description: `<p>${transcript}</p>` };

                    if (prevDesc.endsWith('</p>')) {
                        return { ...prev, description: prevDesc.slice(0, -4) + " " + transcript + "</p>" };
                    }
                    return { ...prev, description: prevDesc + " " + transcript };
                });
            }
        };
        recognition.onerror = () => setIsListening(false);
        recognition.onend = () => setIsListening(false);
        recognition.start();
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.cerName || !formData.description) {
            toast.error(t("Please fill all fields"));
            return;
        }

        setIsSaving(true);
        const token = getAuthToken();
        const apiChar = getApiChara();
        const method = editingId ? "PUT" : "POST";
        const url = editingId
            ? `${apiChar}/certificate/update/${editingId}`
            : `${apiChar}/certificate/add`;

        // Map frontend expected names to backend expected names if different, 
        // but here we align with cerName and description
        const payload = {
            cerId: editingId,
            cerName: formData.cerName,
            description: formData.description,
            todayDate: new Date().getTime() // Include current timestamp
        };

        try {
            const response = await fetch(url, {
                method: method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                toast.success(editingId ? t("Certificate updated successfully!") : t("Certificate added successfully!"));
                setFormData({ cerName: "", description: "" });
                setEditingId(null);
                fetchCertificates();
                fetchCertificateNames();
            } else {
                throw new Error("Failed to save certificate");
            }
        } catch (error) {
            console.error(error);
            toast.error(t("Error saving certificate"));
        } finally {
            setIsSaving(false);
        }
    };

    const handleEdit = (cert) => {
        if (!cert) return;
        const certId = cert.cerId || cert.id;
        // Check all common variations of the certificate name field
        const certName = cert.cerName || cert.cername || cert.certificateName || cert.name || "";
        const certDesc = cert.description || cert.content || "";

        setEditingId(certId);
        setFormData({
            cerName: certName,
            description: certDesc
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleDelete = (id) => {
        setIdToDelete(id);
        setShowDeleteModal(true);
    };

    const confirmDeleteHandler = async () => {
        if (!idToDelete) return;

        const token = getAuthToken();
        const apiChar = getApiChara();
        setLoading(true);
        try {
            const response = await fetch(`${apiChar}/certificate/delete/${idToDelete}`, {
                method: "DELETE",
                headers: { Authorization: "Bearer " + token },
            });
            if (response.ok) {
                toast.success(t("Certificate deleted successfully!"));
                fetchCertificates();
                fetchCertificateNames();
            }
        } catch (error) {
            console.error(error);
            toast.error(t("Error deleting certificate"));
        } finally {
            setLoading(false);
            setShowDeleteModal(false);
            setIdToDelete(null);
        }
    };

    const handleCancel = () => {
        setEditingId(null);
        setFormData({ cerName: "", description: "" });
    };

    const filteredCertificates = filterName
        ? certificates.filter(cert => (cert.cerName || cert.certificateName || cert.name) === filterName)
        : certificates;

    return (
        <div className="certificate-management">
            <Form onSubmit={handleSave} className="mb-4 p-3 border rounded bg-light">
                <h5 className="mb-3" style={{ color: "var(--primary-color)", fontWeight: "700" }}>
                    {editingId ? t("Update Certificate") : t("Add New Certificate")}
                </h5>
                <div className="row g-3">
                    <div className="col-12">
                        <Form.Label style={{ fontWeight: "600", fontSize: "0.85rem" }}>{t("Name")}</Form.Label>
                        <Form.Control
                            type="text"
                            name="cerName"
                            value={formData.cerName}
                            onChange={handleInputChange}
                            placeholder={t("Enter certificate name (e.g., Medical Certificate, Fitness Certificate)")}
                            style={{ fontSize: "0.9rem" }}
                        />
                    </div>
                    <div className="col-12">
                        <Form.Label style={{ fontWeight: "600", fontSize: "0.85rem" }}>{t("Certificate Content")}</Form.Label>
                        <div className="cert-editor-wrapper">
                            <ReactQuill
                                value={formData.description}
                                onChange={handleQuillChange}
                                theme="snow"
                                placeholder={t("Write certificate content here...")}
                            />
                        </div>
                    </div>
                    <div className="col-12 d-flex gap-2">
                        <Button type="submit" className="btn-premium btn-sm px-4" disabled={isSaving}>
                            {isSaving ? <Spinner animation="border" size="sm" className="me-2" /> : (editingId ? <FaSave className="me-2" /> : <FaPlus className="me-2" />)}
                            {editingId ? t("Update") : t("Save")}
                        </Button>
                        <Button
                            type="button"
                            onClick={handleVoiceInput}
                            style={{
                                backgroundColor: isListening ? "#f44336" : "#25d366",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                width: "36px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "all 0.3s"
                            }}
                            title={isListening ? t("Listening...") : t("Voice Input")}
                        >
                            <FaMicrophone style={{ animation: isListening ? "pulse-mic 1s infinite" : "none" }} />
                        </Button>
                        <style>{`
                            @keyframes pulse-mic {
                                0% { transform: scale(1); }
                                50% { transform: scale(1.2); }
                                100% { transform: scale(1); }
                            }
                        `}</style>
                        {editingId && (
                            <Button variant="secondary" size="sm" onClick={handleCancel}>
                                <FaTimes className="me-2" /> {t("Cancel")}
                            </Button>
                        )}
                    </div>
                </div>
            </Form>

            <div className="records-list">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="m-0" style={{ color: "var(--primary-color)", fontWeight: "700" }}>{t("Saved Certificates")}</h5>
                    <div className="d-flex align-items-center gap-2" style={{ maxWidth: "300px" }}>
                        <Form.Select
                            size="sm"
                            style={{ fontSize: "0.85rem" }}
                            value={filterName}
                            onChange={(e) => setFilterName(e.target.value)}
                        >
                            <option value="">{t("Show All Certificates")}</option>
                            {certificateNames.map((name, index) => (
                                <option key={index} value={name}>{name}</option>
                            ))}
                        </Form.Select>
                    </div>
                </div>

                <div className="table-responsive border rounded">
                    <Table striped hover className="mb-0" style={{ fontSize: "0.85rem" }}>
                        <thead className="bg-light">
                            <tr>
                                <th style={{ width: "50px" }}>{t("Sr.No")}</th>
                                <th>{t("Name")}</th>
                                <th style={{ width: "150px" }} className="text-center">{t("Actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="3" className="text-center py-3"><Spinner animation="border" size="sm" /></td></tr>
                            ) : filteredCertificates.length === 0 ? (
                                <tr><td colSpan="3" className="text-center py-3 text-muted">{t("No certificates found")}</td></tr>
                            ) : (
                                filteredCertificates.map((cert, index) => (
                                    <tr key={cert.cerId || cert.id || index}>
                                        <td>{index + 1}</td>
                                        <td className="fw-500">{cert.cerName || cert.certificateName || cert.name}</td>
                                        <td className="text-center">
                                            <Button variant="link" className="text-primary p-1 me-2" onClick={() => handleEdit(cert)}>
                                                <FaEdit />
                                            </Button>
                                            <Button variant="link" className="text-danger p-1" onClick={() => handleDelete(cert.cerId || cert.id)}>
                                                <FaTrash />
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>
            <ConfirmModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={confirmDeleteHandler}
                title={t("Delete Certificate")}
                message={t("Are you sure you want to delete this certificate?")}
                variant="danger"
                isLoading={loading}
            />
        </div>
    );
};

export default CertificateManagement;
